import React from "react";
import { BookOpen, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const ApplyTemplateDialog = ({
  isOpen, setIsOpen,
  templates, templateFormData, setTemplateFormData, templateFormOptions,
  handleTemplateStudyChange, handleTemplateSiteChange, applyTemplate,
}) => (
  <Dialog open={isOpen} onOpenChange={setIsOpen}>
    <DialogTrigger asChild>
      <Button variant="outline" className="flex items-center gap-2" data-testid="apply-template-btn">
        <BookOpen className="h-4 w-4" /> Apply Template
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Apply Task Template</DialogTitle>
        <DialogDescription>Select a template and set Day 0 to create tasks with calculated dates</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label htmlFor="template">Template</Label>
          <Select value={templateFormData.templateId} onValueChange={(value) => setTemplateFormData(prev => ({ ...prev, templateId: value }))}>
            <SelectTrigger><SelectValue placeholder="Select template" /></SelectTrigger>
            <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="study">Study (with auto-population)</Label>
          <Select value={templateFormData.studyId} onValueChange={handleTemplateStudyChange}>
            <SelectTrigger><SelectValue placeholder="Select study" /></SelectTrigger>
            <SelectContent>{templateFormOptions.availableStudies.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.protocol_number && `(${s.protocol_number})`}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="site">Site (with auto-population)</Label>
          <Select value={templateFormData.siteId} onValueChange={handleTemplateSiteChange}>
            <SelectTrigger><SelectValue placeholder="Select site" /></SelectTrigger>
            <SelectContent>{templateFormOptions.availableSites.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.coordinator && `(${s.coordinator})`}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="dayZero">Day 0 Date</Label>
          <Input id="dayZero" type="date" value={templateFormData.dayZeroDate} onChange={(e) => setTemplateFormData(prev => ({ ...prev, dayZeroDate: e.target.value }))} className="w-full" />
          <p className="text-xs text-gray-600 mt-1">Reference date for calculating task dates</p>
        </div>
        <div>
          <Label htmlFor="visit">Visit</Label>
          <Input id="visit" value={templateFormData.visit} onChange={(e) => setTemplateFormData(prev => ({ ...prev, visit: e.target.value }))} placeholder="Enter visit information" className="w-full" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { setIsOpen(false); setTemplateFormData({ templateId: '', studyId: '', siteId: '', dayZeroDate: '', visit: '' }); }}>Cancel</Button>
        <Button onClick={applyTemplate} data-testid="apply-template-submit">Apply Template</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const NewTaskDialog = ({
  isOpen, setIsOpen, newTaskData, setNewTaskData, sites, studies, users, handleNewTaskSubmit,
}) => (
  <Dialog open={isOpen} onOpenChange={setIsOpen}>
    <DialogTrigger asChild>
      <Button className="flex items-center gap-2" data-testid="new-task-btn">
        <Plus className="h-4 w-4" /> New Task
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Create New Task</DialogTitle>
        <DialogDescription>Add a new task to your study workflow</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleNewTaskSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="task-title">Title *</Label>
            <Input id="task-title" value={newTaskData.title} onChange={(e) => setNewTaskData({ ...newTaskData, title: e.target.value })} placeholder="Enter task title" required />
          </div>
          <div>
            <Label htmlFor="task-priority">Priority</Label>
            <Select value={newTaskData.priority} onValueChange={(value) => setNewTaskData({ ...newTaskData, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="task-description">Description</Label>
          <Textarea id="task-description" value={newTaskData.description} onChange={(e) => setNewTaskData({ ...newTaskData, description: e.target.value })} placeholder="Enter task description" rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="task-study">Study *</Label>
            <Select value={newTaskData.study_id} onValueChange={(value) => setNewTaskData({ ...newTaskData, study_id: value })}>
              <SelectTrigger><SelectValue placeholder="Select study" /></SelectTrigger>
              <SelectContent>{studies.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="task-site">Site *</Label>
            <Select value={newTaskData.site_id} onValueChange={(value) => setNewTaskData({ ...newTaskData, site_id: value })}>
              <SelectTrigger><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="task-assignee">Assignee *</Label>
          <Select value={newTaskData.assignee} onValueChange={(value) => setNewTaskData({ ...newTaskData, assignee: value })}>
            <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
            <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.name}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="task-visit">Visit</Label>
          <Input id="task-visit" value={newTaskData.visit} onChange={(e) => setNewTaskData({ ...newTaskData, visit: e.target.value })} placeholder="Enter visit information (optional)" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="task-planned-date">Planned Date *</Label>
            <Input id="task-planned-date" type="date" value={newTaskData.planned_date} onChange={(e) => setNewTaskData({ ...newTaskData, planned_date: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="task-sliding-date">Sliding Date</Label>
            <Input id="task-sliding-date" type="date" value={newTaskData.sliding_date} onChange={(e) => setNewTaskData({ ...newTaskData, sliding_date: e.target.value })} />
            <p className="text-xs text-gray-500 mt-1">Optional - Use only when planned date needs to slip</p>
          </div>
        </div>
      </form>
      <DialogFooter>
        <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
        <Button onClick={handleNewTaskSubmit} data-testid="create-task-submit">Create Task</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
